/**
 * Loads the signed-in seller's Sellwild fee rate.
 *
 * Shared by the sell page and my-listings so both surfaces quote the same
 * number. Returns null while loading and on failure — every caller renders
 * nothing rather than guessing at 10%, because a stale or assumed rate is worse
 * than no rate at all when the seller is deciding how to price something.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  SellerFeeStatusResponseSchema,
  type SellerFeeStatus,
} from '@/schemas/api/seller/fee-status';

interface UseSellerFeeStatusResult {
  feeStatus: SellerFeeStatus | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Plain fetch, no React state. Kept outside the hook so the effect below can
 * await it before touching state — calling setState synchronously inside an
 * effect body triggers cascading renders.
 */
async function fetchFeeStatus(): Promise<SellerFeeStatus | null> {
  try {
    const response = await fetch('/api/seller/fee-status');
    if (!response.ok) return null;

    const body = await response.json();
    const parsed = SellerFeeStatusResponseSchema.safeParse(body);
    return parsed.success ? parsed.data.feeStatus : null;
  } catch {
    // A missing fee badge is a cosmetic loss; surfacing an error here would
    // interrupt someone in the middle of listing an item.
    return null;
  }
}

export function useSellerFeeStatus(enabled: boolean = true): UseSellerFeeStatusResult {
  const [feeStatus, setFeeStatus] = useState<SellerFeeStatus | null>(null);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;

    // `cancelled` guards against a resolve landing after the seller navigated
    // away mid-request, which would set state on an unmounted component.
    let cancelled = false;

    void (async () => {
      const status = await fetchFeeStatus();
      if (cancelled) return;
      setFeeStatus(status);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    const status = await fetchFeeStatus();
    setFeeStatus(status);
    setLoading(false);
  }, [enabled]);

  // Derived rather than cleared in the effect: when `enabled` flips false (the
  // seller signed out) the stale rate must stop showing immediately, and
  // clearing it via setState inside the effect would cascade a render.
  return { feeStatus: enabled ? feeStatus : null, loading, refresh };
}
